import { useState } from "react";
import { Paper, Typography, Grid, Box, Chip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import TodayIcon from "@mui/icons-material/Today";
import EventIcon from "@mui/icons-material/Event";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import Loading from "../ui/Loading";
import OpsHealthCallsModal from "./OpsHealthCallsModal";

const OpsHealthCard = ({ data, isLoading, error, filters, onRefresh }) => {
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);

  if (isLoading) return <Loading />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!data) return <Typography>No data available</Typography>;

  const metrics = [
    {
      label: "Incomplete",
      value: data.incompleteCount || 0,
      color: theme.palette.warning.main,
      icon: <ErrorOutlineIcon />,
      type: "incomplete",
    },
    {
      label: "Completed",
      value: data.completedCount || 0,
      color: theme.palette.success.dark,
      icon: <TaskAltIcon />,
      type: "completed",
    },
    {
      label: "Today",
      value: data.scheduledToday || 0,
      color: theme.palette.info.main,
      icon: <TodayIcon />,
      type: "today",
    },
    {
      label: "Tomorrow",
      value: data.scheduledTomorrow || 0,
      color: theme.palette.info.light,
      icon: <EventIcon />,
      type: "tomorrow",
    },
    {
      label: "Rest of Month",
      value: data.scheduledRestOfMonth || 0,
      color: theme.palette.success.main,
      icon: <CalendarMonthIcon />,
      type: "restOfMonth",
    },
    {
      label: "Overdue",
      value: data.overdueCount || 0,
      color: theme.palette.error.main,
      icon: <WarningAmberIcon />,
      type: "overdue",
    },
    {
      label: "Unassigned",
      value: data.unassignedCount || 0,
      color: theme.palette.grey[600],
      icon: <PersonOffIcon />,
      type: "unassigned",
    },
  ];

  const handleMetricClick = (metricType) => {
    setSelectedMetric(metricType);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedMetric(null);
    // Refresh metrics after modal closes (in case data changed)
    if (onRefresh) {
      onRefresh();
    }
  };

  return (
    <Paper sx={{ p: 2, height: "100%", width: "100%" }}>
      <Typography
        variant="h6"
        gutterBottom
      >
        Operations Health
      </Typography>
      <Grid
        container
        spacing={2}
        sx={{ width: "100%" }}
      >
        {metrics.map((metric, index) => (
          <Grid
            size={{ xs: 12, sm: 6, md: 2 }}
            key={index}
            sx={{ display: "flex" }}
          >
            <Box
              onClick={() => handleMetricClick(metric.type)}
              sx={{
                textAlign: "center",
                p: 2,
                borderRadius: 1,
                backgroundColor: `${metric.color}15`,
                border: `1px solid ${metric.color}`,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: `${metric.color}30`,
                  transform: "translateY(-2px)",
                  boxShadow: 2,
                },
              }}
            >
              <Box sx={{ color: metric.color, mb: 1 }}>{metric.icon}</Box>
              <Typography
                variant="h4"
                sx={{ color: metric.color, fontWeight: "bold" }}
              >
                {metric.value}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
              >
                {metric.label}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      <OpsHealthCallsModal
        open={modalOpen}
        onClose={handleModalClose}
        metricType={selectedMetric}
        filters={filters}
      />
    </Paper>
  );
};

export default OpsHealthCard;
